import { Link, useLocation } from 'react-router-dom';
import { Home, Package, RotateCcw } from 'lucide-react';

/**
 * Persistent bottom tab bar — always visible, every PWA screen.
 *
 *   Home     /pwa
 *   Pack     /pwa/pack    (active also on /pwa/pack/:id)
 *   Returns  /pwa/returns (active also on /pwa/returns/:id)
 *
 * On scan screens the per-page bottom drawer renders ABOVE this tab
 * bar via `bottom: var(--pwa-tab-bar-h)` so the two stack rather
 * than fight for the same thumb space.
 */
export function PwaTabBar() {
  const location = useLocation();
  const path = location.pathname;

  const onPack = path === '/pwa/pack' || path.startsWith('/pwa/pack/');
  const onReturns = path === '/pwa/returns' || path.startsWith('/pwa/returns/');
  const onHome = path === '/pwa';

  return (
    <nav
      className="pwa-pb-safe pwa-pl-safe pwa-pr-safe pwa-chrome fixed inset-x-0 bottom-0 z-30 border-t border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/85"
      style={{ minHeight: 'calc(var(--pwa-tab-bar-h) + env(safe-area-inset-bottom))' }}
      aria-label="Primary"
    >
      <div className="grid grid-cols-3">
        <Tab
          to="/pwa"
          icon={<Home className="h-5 w-5" />}
          label="Home"
          active={onHome}
        />
        <Tab
          to="/pwa/pack"
          icon={<Package className="h-5 w-5" />}
          label="Pack"
          active={onPack}
        />
        <Tab
          to="/pwa/returns"
          icon={<RotateCcw className="h-5 w-5" />}
          label="Returns"
          active={onReturns}
        />
      </div>
    </nav>
  );
}

function Tab({
  to,
  icon,
  label,
  active,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  active: boolean;
}) {
  return (
    <Link
      to={to}
      className={`pwa-tap flex flex-col items-center justify-center gap-0.5 px-2 py-2.5 text-[10.5px] font-medium ${
        active
          ? 'text-primary'
          : 'text-muted-foreground active:text-foreground'
      }`}
    >
      <span
        className={`grid h-7 w-12 place-items-center rounded-full ${
          active ? 'bg-primary/15' : ''
        }`}
      >
        {icon}
      </span>
      <span>{label}</span>
    </Link>
  );
}
